/**
 * Indigo DropdownSelectChecklist for the design system.
 * A single-select dropdown whose options list ends with a pinned checkbox
 * row that expands (on hover) into a second-level checklist flyout.
 *
 * Built for "pick one value, additionally overlay several" controls — e.g.
 * the scheduler's location picker: choose the appointment location from the
 * list, and tick "Show all apts at" locations in the flyout to overlay
 * other locations' appointments.
 *
 * Usage:
 *   <DropdownSelectChecklist
 *     menuTitle="Schedule apt at:"
 *     options={locationOptions}
 *     value={selectedLocation}
 *     onChange={setSelectedLocation}
 *     checklistLabel="Show all apts at"
 *     checklistSelectAllLabel="All locations for this physician"
 *     checklistValue={overlayLocations}
 *     onChecklistChange={setOverlayLocations}
 *   />
 */
import { clsx } from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import Checkbox from "../Checkbox/Checkbox";
import { Icon } from "../Icons/Icons";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import "./DropdownSelectChecklist.css";

const SUBMENU_CLOSE_DELAY = 150;

export interface DropdownSelectChecklistOption<T extends string = string> {
  value: T;
  label: string;
  /** When true, the option is shown but not selectable. */
  disabled?: boolean;
}

export interface DropdownSelectChecklistProps<T extends string = string> {
  /** Heading rendered at the top of the panel (e.g. "Schedule apt at:"). */
  menuTitle?: string;
  /** Options of the single-select list. */
  options: DropdownSelectChecklistOption<T>[];
  /** Currently selected value of the single-select list. */
  value: T | null;
  onChange: (value: T | null) => void;
  placeholder?: string;

  /** Label of the pinned checkbox row that opens the checklist flyout. */
  checklistLabel: string;
  /** When provided, the flyout gets a pinned "select all" checkbox. */
  checklistSelectAllLabel?: string;
  /**
   * Options of the checklist flyout. Defaults to `options` when omitted.
   */
  checklistOptions?: DropdownSelectChecklistOption<T>[];
  /** Currently checked values of the checklist flyout. */
  checklistValue: T[];
  onChecklistChange: (value: T[]) => void;
  /** Shows a spinner on the checklist row while its data loads. */
  checklistLoading?: boolean;

  /**
   * Size of the trigger
   * - "small": 22px height
   * - "default": 32px height
   * - "large": 40px height
   * @default "default"
   */
  size?: "small" | "default" | "large";
  disabled?: boolean;
  /** Test ID for the trigger; panel and flyout get `<testid>-panel` / `<testid>-flyout`. */
  "data-testid"?: string;
}

export function DropdownSelectChecklist<T extends string = string>({
  menuTitle,
  options,
  value,
  onChange,
  placeholder = "Select one",
  checklistLabel,
  checklistSelectAllLabel,
  checklistOptions,
  checklistValue,
  onChecklistChange,
  checklistLoading = false,
  size = "default",
  disabled = false,
  "data-testid": dataTestId,
}: DropdownSelectChecklistProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const flyoutCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const flyoutOptions = checklistOptions ?? options;
  const checkedSet = useMemo(() => new Set(checklistValue), [checklistValue]);
  const selectableFlyoutValues = useMemo(
    () => flyoutOptions.filter((o) => !o.disabled).map((o) => o.value),
    [flyoutOptions],
  );
  const checkedCount = useMemo(
    () => flyoutOptions.filter((o) => checkedSet.has(o.value)).length,
    [flyoutOptions, checkedSet],
  );
  const allChecked = useMemo(
    () =>
      selectableFlyoutValues.length > 0 &&
      selectableFlyoutValues.every((v) => checkedSet.has(v)),
    [selectableFlyoutValues, checkedSet],
  );

  const selectedLabel =
    options.find((o) => o.value === value)?.label ?? placeholder;

  const closeAll = () => {
    setIsFlyoutOpen(false);
    setIsOpen(false);
  };

  // Close on outside click / Escape (flyout first, then the panel)
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        closeAll();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setIsFlyoutOpen((flyoutWasOpen) => {
        if (!flyoutWasOpen) {
          setIsOpen(false);
          // Return focus to the trigger so keyboard users aren't stranded
          triggerRef.current?.focus();
        }
        return false;
      });
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Clean up the pending flyout-close timer on unmount
  useEffect(
    () => () => {
      if (flyoutCloseTimeoutRef.current) {
        clearTimeout(flyoutCloseTimeoutRef.current);
      }
    },
    [],
  );

  const openFlyout = () => {
    if (flyoutCloseTimeoutRef.current) {
      clearTimeout(flyoutCloseTimeoutRef.current);
      flyoutCloseTimeoutRef.current = null;
    }
    setIsFlyoutOpen(true);
  };

  // Delay so the cursor can travel from the row into the flyout
  const scheduleFlyoutClose = () => {
    flyoutCloseTimeoutRef.current = setTimeout(() => {
      setIsFlyoutOpen(false);
      flyoutCloseTimeoutRef.current = null;
    }, SUBMENU_CLOSE_DELAY);
  };

  const handleSelect = (option: DropdownSelectChecklistOption<T>) => {
    if (option.disabled) return;
    onChange(option.value);
    closeAll();
  };

  const toggleChecklistOption = (optionValue: T, checked: boolean) => {
    onChecklistChange(
      checked
        ? [...checklistValue, optionValue]
        : checklistValue.filter((v) => v !== optionValue),
    );
  };

  const toggleChecklistAll = (checked: boolean) => {
    onChecklistChange(checked ? [...selectableFlyoutValues] : []);
  };

  return (
    <div
      ref={containerRef}
      className={clsx(
        "indigo-dropdown-select-checklist",
        `indigo-dropdown-select-checklist--${size}`,
        {
          "is-open": isOpen,
          "is-disabled": disabled,
        },
      )}
    >
      <button
        ref={triggerRef}
        type="button"
        className="indigo-dropdown-select-checklist__trigger"
        onClick={() => {
          if (disabled) return;
          setIsFlyoutOpen(false);
          setIsOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid={dataTestId}
      >
        <span className="indigo-dropdown-select-checklist__trigger-label">
          {selectedLabel}
        </span>
        <Icon
          name="down-chevron"
          size="s"
          className={clsx("indigo-dropdown-select-checklist__arrow", {
            "is-open": isOpen,
          })}
        />
      </button>

      {isOpen && (
        <div
          className="indigo-dropdown-select-checklist__panel"
          data-testid={dataTestId ? `${dataTestId}-panel` : undefined}
        >
          {menuTitle && (
            <div className="indigo-dropdown-select-checklist__title">
              {menuTitle}
            </div>
          )}

          {/*
            Styled as a menu of buttons rather than an ARIA listbox: each
            option is a real <button> (natively focusable, announced), and
            selection is conveyed via aria-pressed. A listbox role would be
            invalid here because role="option" may not wrap interactive
            descendants.
          */}
          <ul className="indigo-dropdown-select-checklist__options">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    className={clsx(
                      "indigo-dropdown-select-checklist__option",
                      {
                        "is-selected": isSelected,
                        "is-disabled": option.disabled,
                      },
                    )}
                    onClick={() => handleSelect(option)}
                    disabled={option.disabled}
                    aria-pressed={isSelected}
                  >
                    <span className="indigo-dropdown-select-checklist__option-label">
                      {option.label}
                    </span>
                    {isSelected && <Icon name="check" size="s" />}
                  </button>
                </li>
              );
            })}
            {options.length === 0 && (
              <li className="indigo-dropdown-select-checklist__empty">
                No options.
              </li>
            )}
          </ul>

          <div
            className="indigo-dropdown-select-checklist__footer"
            onMouseEnter={openFlyout}
            onMouseLeave={scheduleFlyoutClose}
            onFocus={openFlyout}
          >
            <Checkbox
              label={checklistLabel}
              checked={allChecked}
              indeterminate={checkedCount > 0 && !allChecked}
              disabled={selectableFlyoutValues.length === 0}
              onChange={(e) => toggleChecklistAll(e.target.checked)}
              size="sm"
            />
            {checklistLoading && (
              <LoadingSpinner
                size={14}
                ariaLabel="Loading appointments"
                className="indigo-dropdown-select-checklist__spinner"
              />
            )}

            {/* A group of native checkboxes — their built-in semantics carry
                the checked state, so no listbox/option roles are needed. */}
            <div
              className={clsx("indigo-dropdown-select-checklist__flyout", {
                "is-open": isFlyoutOpen,
              })}
              role="group"
              aria-label={checklistLabel}
              data-testid={dataTestId ? `${dataTestId}-flyout` : undefined}
            >
              {checklistSelectAllLabel && (
                <div className="indigo-dropdown-select-checklist__flyout-item indigo-dropdown-select-checklist__flyout-item--select-all">
                  <Checkbox
                    label={checklistSelectAllLabel}
                    checked={allChecked}
                    indeterminate={checkedCount > 0 && !allChecked}
                    disabled={selectableFlyoutValues.length === 0}
                    onChange={(e) => toggleChecklistAll(e.target.checked)}
                    size="sm"
                  />
                </div>
              )}
              {flyoutOptions.map((option) => (
                <div
                  key={option.value}
                  className="indigo-dropdown-select-checklist__flyout-item"
                >
                  <Checkbox
                    label={option.label}
                    checked={checkedSet.has(option.value)}
                    disabled={option.disabled}
                    onChange={(e) =>
                      toggleChecklistOption(option.value, e.target.checked)
                    }
                    size="sm"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

DropdownSelectChecklist.displayName = "DropdownSelectChecklist";

export default DropdownSelectChecklist;
